// Original TouchGFX imagery only: a byte-preserved recorded simulator screenshot
// or bounded RGB24 frames supplied by the parent. Never starts a runtime/hardware.
export const controllerDisplayScreenshot = Object.freeze({
  path: "/feeder-assets/touchgfx-original-screen1-20261014.png",
  sha256: "4b0aa367cccd58a32eee744101d043dc63022d3ffd3ae16fd565a1db204281f4",
  width: 480, height: 272, recordedAt: "2026-10-14",
});
export const controllerDisplayScreenshotNotice = "Original-TouchGFX-Screenshot · statische Simulation vom 14.10.2026, keine Live-Messwerte und nicht bedienbar.";
export const controllerDisplayLayout = Object.freeze({ width: 480, height: 272, drawingHeight: .89, drawingWidth: .89 * 480 / 272, z: -.0535 });
const pixelBytes = 480 * 272 * 3;
function localScreenshotUrl(value) {
  const base = typeof window !== "undefined" ? window.location.href : "http://localhost/";
  const url = new URL(value, base);
  if (typeof value !== "string" || url.origin !== new URL(base).origin
      || !url.pathname.endsWith(controllerDisplayScreenshot.path)
      || url.username || url.password || url.search || url.hash) throw new Error("Display requires the local original screenshot");
  return url.href;
}
export function isControllerDisplayFrame(frame) {
  return Boolean(frame && frame.width === 480 && frame.height === 272
    && frame.pixels instanceof Uint8Array && frame.pixels.length === pixelBytes
    && Number.isInteger(frame.sequence) && frame.sequence >= 1 && frame.sequence <= 2147483647);
}
export function displayUvToPixel(uv) {
  if (!uv || !Number.isFinite(uv.x) || !Number.isFinite(uv.y) || uv.x < 0 || uv.x > 1 || uv.y < 0 || uv.y > 1) return null;
  return { x: Math.min(479, Math.floor(uv.x * 480)), y: Math.min(271, Math.floor((1 - uv.y) * 272)) };
}
export function findVisibleDisplayIntersection(intersections, surface) {
  for (const hit of intersections) {
    let visible = true, object = hit.object;
    while (object) { if (!object.visible) visible = false; object = object.parent; }
    if (!visible || !hit.object.isMesh) continue;
    const materials = Array.isArray(hit.object.material) ? hit.object.material : [hit.object.material];
    if (materials.every(mat => mat.transparent && mat.opacity < .3)) continue;
    return hit.object === surface && displayUvToPixel(hit.uv) ? hit : null;
  }
  return null;
}

/** Mesh/material ownership belongs to the caller's registry; texture to us. */
export function createControllerDisplaySurface(THREE, { mesh }) {
  if (typeof mesh !== "function") throw new TypeError("Display surface requires registered meshes");
  const root = new THREE.Group(); root.name = "controller-original-display";
  const material = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false, fog: false });
  const surface = mesh(new THREE.PlaneGeometry(controllerDisplayLayout.drawingWidth, controllerDisplayLayout.drawingHeight), material, root, [0, 0, controllerDisplayLayout.z]);
  surface.rotation.y = Math.PI; surface.visible = false;
  surface.name = "controller-original-display-pixels"; surface.userData.controllerDisplay = true;
  surface.castShadow = false; surface.receiveShadow = false;
  let texture = null, sequence = 0, disposed = false, ready = false;
  let screenshot = null, screenshotStatus = "unloaded", generation = 0;
  const pendingScreenshots = new Set(), disposedScreenshots = new WeakSet();
  root.userData = { displayMode: "empty", screenshotSha256: controllerDisplayScreenshot.sha256, notice: controllerDisplayScreenshotNotice };
  const discardScreenshot = value => {
    if (!value?.isTexture || disposedScreenshots.has(value)) return;
    disposedScreenshots.add(value); pendingScreenshots.delete(value); value.dispose();
  };
  const showScreenshot = () => {
    if (ready) return;
    material.map = disposed ? null : screenshot; surface.visible = Boolean(material.map);
    root.userData.displayMode = material.map ? "static-screenshot" : "empty"; material.needsUpdate = true;
  };
  const clear = () => { sequence = 0; ready = false; showScreenshot(); };
  function loadScreenshot({ url = controllerDisplayScreenshot.path, onChange = () => {}, loader } = {}) {
    if (disposed) return false;
    const sourceUrl = localScreenshotUrl(url), current = ++generation;
    [...pendingScreenshots].forEach(discardScreenshot);
    discardScreenshot(screenshot); screenshot = null; screenshotStatus = "loading"; showScreenshot();
    const notify = () => { try { onChange(screenshotStatus); } catch (_) { /* Rendering observers do not own cleanup. */ } };
    const fail = () => {
      if (disposed || current !== generation) return;
      generation += 1; [...pendingScreenshots].forEach(discardScreenshot);
      screenshot = null; screenshotStatus = "unavailable"; showScreenshot(); notify();
    };
    const loaded = value => {
      if (disposed || current !== generation) { discardScreenshot(value); return; }
      const image = value?.image;
      if (!value?.isTexture || (image?.naturalWidth || image?.width) !== 480
          || (image?.naturalHeight || image?.height) !== 272) { discardScreenshot(value); fail(); return; }
      pendingScreenshots.add(value);
      value.colorSpace = THREE.SRGBColorSpace; value.flipY = true;
      value.minFilter = THREE.LinearFilter; value.magFilter = THREE.LinearFilter;
      value.wrapS = THREE.ClampToEdgeWrapping; value.wrapT = THREE.ClampToEdgeWrapping;
      value.generateMipmaps = false; value.needsUpdate = true;
      screenshot = value; screenshotStatus = "ready"; showScreenshot(); notify();
    };
    try {
      const request = (loader || new THREE.TextureLoader()).load(sourceUrl, loaded, undefined, fail);
      if (request?.isTexture) {
        if (disposed || current !== generation) discardScreenshot(request);
        else pendingScreenshots.add(request);
      }
    } catch (_) { fail(); }
    return true;
  }
  return {
    root, surface, loadScreenshot,
    get ready() { return ready; },
    get sequence() { return sequence; },
    get screenshotStatus() { return screenshotStatus; },
    setFrame(frame) {
      if (disposed) return false;
      if (!isControllerDisplayFrame(frame)) { clear(); return false; }
      if (frame.sequence <= sequence) return false;
      if (!texture) {
        // Three r170 requires RGBA8 for its sRGB WebGL2 upload. The external
        // contract remains RGB24; this single reusable GPU buffer adds alpha.
        texture = new THREE.DataTexture(new Uint8Array(480 * 272 * 4), 480, 272, THREE.RGBAFormat, THREE.UnsignedByteType);
        texture.colorSpace = THREE.SRGBColorSpace; texture.flipY = true; texture.unpackAlignment = 1;
        texture.minFilter = THREE.LinearFilter; texture.magFilter = THREE.LinearFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping; texture.wrapT = THREE.ClampToEdgeWrapping; texture.generateMipmaps = false;
      }
      const data = texture.image.data;
      for (let src = 0, dst = 0; src < pixelBytes; src += 3, dst += 4) {
        data[dst] = frame.pixels[src]; data[dst + 1] = frame.pixels[src + 1]; data[dst + 2] = frame.pixels[src + 2]; data[dst + 3] = 255;
      }
      texture.needsUpdate = true;
      material.map = texture; material.needsUpdate = true; surface.visible = true;
      sequence = frame.sequence; ready = true; root.userData.displayMode = "live-simulator-frame"; return true;
    },
    clear,
    dispose() {
      if (disposed) return;
      disposed = true; generation += 1; clear();
      if (texture) texture.dispose(); texture = null;
      [...pendingScreenshots].forEach(discardScreenshot); discardScreenshot(screenshot); screenshot = null; screenshotStatus = "disposed";
    }
  };
}
